/**
 * DUE-471: the one place that knows how to ask for AI consent mid-session.
 *
 * The server answers every LLM-backed endpoint with
 * `403 {"error": {"code": "ai_consent_required"}}` until the user has agreed.
 * `request()` (api/client.ts) catches that, calls `requestAiConsent()` and
 * replays the request once the user agrees. The UI that actually asks is
 * `ConsentHost`, which registers itself here — this module imports nothing
 * from the API layer, so the client can depend on it without a cycle.
 */

export type AiConsentPrompter = () => Promise<boolean>;

let prompter: AiConsentPrompter | null = null;
let pending: Promise<boolean> | null = null;
/* "Not now" is remembered until `clearAiConsentDecline()` — called by the
 * explicit "agree after all" action (`reviewAiConsent`) — or until a prompter
 * is registered again: otherwise react-query's window-focus refetch would pop
 * the sheet again straight after a refusal. */
let declined = false;

export function setAiConsentPrompter(next: AiConsentPrompter | null): void {
  prompter = next;
  pending = null;
  declined = false;
}

/** True when `body` is the project's error envelope carrying the consent code. */
export function isAiConsentRequired(body: unknown): boolean {
  if (typeof body !== "object" || body === null) return false;
  const error = (body as { error?: unknown }).error;
  if (typeof error !== "object" || error === null) return false;
  return (error as { code?: unknown }).code === "ai_consent_required";
}

/** An error that came from the consent gate — duck-typed, because this module
 * must not import `ApiError` (the client imports this one). */
export function isAiConsentError(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;
  const e = error as { status?: unknown; body?: unknown };
  return e.status === 403 && isAiConsentRequired(e.body);
}

/** react-query `retry` predicate: never retry the consent gate (a retry is
 * the same refusal, and each one would reopen the sheet); otherwise up to
 * `max` retries. */
export function retryUnlessConsent(max: number) {
  return (failureCount: number, error: unknown): boolean =>
    !isAiConsentError(error) && failureCount < max;
}

export function isAiConsentDeclined(): boolean {
  return declined;
}

export function clearAiConsentDecline(): void {
  declined = false;
}

/** Resolves true once the user has agreed. Parallel callers share one prompt. */
export function requestAiConsent(): Promise<boolean> {
  if (!prompter || declined) return Promise.resolve(false);
  if (!pending) {
    pending = prompter()
      .then((agreed) => {
        declined = !agreed;
        return agreed;
      })
      .finally(() => {
        pending = null;
      });
  }
  return pending;
}
